import React, {
  Fragment,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import {
  Select,
  Typography,
  Row,
  Col,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Table,
  Skeleton,
  Radio,
  Checkbox,
  Tooltip,
  message,
  Spin,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  getShippingRules,
  saveShippingRule,
  deleteShippingRule,
  getStatesProvinces,
  getCarrServices,
  getCustomerGroups,
} from '../../Actions/ShippingRulesActions';
import addKeysToList from './../../Utilities/addKey';
import types from '../../Stores/types';
import axios from '../../Utilities/authToken';
import { dispatchAlert } from '../../Utilities/dispatchAlert';
import { accessorialServices, shippingRuleTypes } from '../../Utilities/constants';
import {
  blockInvalidChar,
  handleNumbersOnly,
} from '../../Utilities/numberValidation';
import ServiceRate from '../ServiceRate';
import LargeCartSettings from '../LargeCartSettings';
import BackupRateRule from './ShippingRules/BackupRateRule';

const { Title } = Typography;
const { Option } = Select;
const initialState = {
  rule_name: '',
  label_as: '',
  filter_country: [],
  rule_type: '10',
  apply_rule_to: 1,
  filter_settings: [],
  filter_products: [],
  filter_categories: [],
  filter_brands: [],
  warehouses: [],
  filter_state_province: [],
  filter_postal_code: [],
  filter_provider: [],
  filter_services: [],
  service_rates: null,
  service_residential_fee: null,
  service_liftgate_fee: null,
  service_notify_fee: null,
  weight_from: '',
  weight_to: '',
  price_from: '',
  price_to: '', 
  priceBasis: 'regular',
  flat_rate: '',
  x_quantity: '',
  percentage_of_cart_price: '',
  backup_rate_method: undefined,
  quantity_from: '',
  quantity_to: '',
  max_items: '',
  max_package_weight: '',
  filter_flat_shipping_rate: '',
  filter_max_shipping_rate: '',
  filter_logic: 'all',
  is_filter_category: false,
  is_filter_brand: false,
  is_filter_product: false,
  is_filter_customer_group: false,
  filter_customer_groups: [],
  is_address_type: false,
  selected_address_type: 0,
  number_of_transit_days: '',
  ground_metric: 0,
};

function ShippingRulesComponent() {
  const [modal, setModal] = useState({
    open: false,
    type: '',
  });
  const [shippingRuleId, setShippingRuleId] = useState(null);
  const [available, setAvailable] = useState(true);
  const [countryCode, setCountryCode] = useState([]); // For Filter by Location (multiple)
  const [mainCountry, setMainCountry] = useState('US'); // For main Countries dropdown (single)
  const [latestCountry, setLatestCountry] = useState('US');
  const [isFilterWeight, setIsFilterWeight] = useState(false);
  const [isFilterPrice, setIsFilterPrice] = useState(false);
  const [isFilterQuantity, setIsFilterQuantity] = useState(false);
  const [isFilterFlatPrice, setIsFilterFlatPrice] = useState(false);
  const [isFilterCategory, setIsFilterCategory] = useState(false);
  const [isFilterBrand, setIsFilterBrand] = useState(false);
  const [isFilterProduct, setIsFilterProduct] = useState(false);
  const [isFilterLocation, setIsFilterLocation] = useState(false);
  const [isFilterMaxShippingRate, setIsFilterMaxShippingRate] = useState(false);
  const [isAddressType, setIsAddressType] = useState(false);
  const [selectedAddressType, setSelectedAddressType] = useState(0);
  const [isFilterCustomerGroup, setIsFilterCustomerGroup] = useState(false);
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState([]);
  const [filterLogic, setFilterLogic] = useState('all');
  const [ruleType, setRuleType] = useState(10);
  const [applyRuleTo, setApplyRuleTo] = useState(1);
  const [applyTo, setApplyTo] = useState(1);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    alertMessageType,
    shippingRules,
    token,
    installedCarriers,
    statesProvinces,
    warehouse,
    dropships,
    storeCategories,
    storeBrands,
    customerGroups,
    store,
    radSettings,
    radPlans,
  } = useSelector((state) => state);
  const [selectedServices, setSelectedServices] = useState([]);
  const [carrierId, setCarrierId] = useState();
  const [carrierSlug, setCarrierSlug] = useState(null);
  // Locally-merged named parcel services for the Service dropdown - kept separate from the
  // shared carrierServices redux state, which gets overwritten wholesale on every dispatch
  // and would otherwise let one provider's fetch clobber another's when several are
  // selected at once (Hide Methods' multi-select Provider field).
  const [carrierServicesLocal, setCarrierServicesLocal] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [combinedStatesProvinces, setCombinedStatesProvinces] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [selectedPostalCodes, setSelectedPostalCodes] = useState([]);
  const [recordId, setRecordId] = useState(null);
  const [accessorials, setAccessorials] = useState([]);
  const [isLTL, setIsLTL] = useState();
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef();

  // Compute warehouse-based and dropship-based provider options (in-store pickup & local delivery).
  // Only enabled options are included; disabled ones are intentionally omitted so the provider
  // field shows empty rather than a stale raw key when instore/local delivery is turned off.
  const warehouseProviderOptions = useMemo(() => {
    const options = [];
    const allLocations = [...(warehouse || []), ...(dropships || [])];
    allLocations.forEach(w => {
      try {
        const additionals = typeof w.additionals === 'string'
          ? JSON.parse(w.additionals)
          : (w.additionals || {});
        if (additionals.instore_pickup) {
          const desc = additionals.instore_pickup_data?.checkout_description;
          options.push({
            value: `instore_${w.id}`,
            label: `${desc || 'In-Store Pickup'} | ${w.city}, ${w.state}, ${w.zip_code}`,
          });
        }
        if (additionals.local_delivery) {
          const desc = additionals.local_delivery_data?.checkout_description;
          options.push({
            value: `local_delivery_${w.id}`,
            label: `${desc || 'Local Delivery'} | ${w.city}, ${w.state}, ${w.zip_code}`,
          });
        }
      } catch (e) {}
    });
    return options;
  }, [warehouse, dropships]);

  // To show location filter in override rule for specific store
  const urlParams = new URLSearchParams(window.location.search);
  const storeParam = urlParams.get('store'); // hash in production

  const showOverrideRule =
    store?.id === 'd7uldtcemt' ||
    store?.id === 'pos5nwk4rp' ||
    store?.id === 'lvadff42ep' ||
    store?.store_id === 329 ||
    store?.store_id === 354 ||
    store?.store_id === 250 ||
    storeParam === 'd7uldtcemt' ||
    storeParam === 'pos5nwk4rp' ||
    storeParam === 'lvadff42ep';

  useEffect(() => {
    if (!shippingRules) {
      dispatch(getShippingRules(token));
    }

    if (!customerGroups || customerGroups.length === 0) {
      dispatch(getCustomerGroups(token));
    }

    if (ruleType == 2 || ruleType == 3 || ruleType == 4 || ruleType == 6 || ruleType == 8 || ruleType == 10) {
      // Get states for the latest selected country
      dispatch(getStatesProvinces(latestCountry, token));
    }

  }, [dispatch, shippingRules, token, latestCountry, ruleType]);

  // Fetch states for all selected countries in Filter by Location
  useEffect(() => {
    const fetchStatesForAllCountries = async () => {
      // Only fetch if Filter by Location is enabled and countries are selected
      if (isFilterLocation && countryCode && countryCode.length > 0) {
        try {
          const allStates = [];
          let successCount = 0;

          // Fetch states for each selected country in order
          for (const country of countryCode) {
            const url = `${process.env.REACT_APP_ENITURE_API_URL}/get_country_states`;
            const config = {
              headers: {
                authorization: `Bearer ${token}`,
              },
            };
            const reqData = { countryCode: country };

            const response = await axios.post(url, reqData, config);

            if (!response.data.error && response.data.data) {
              // Add states from this country to the combined list
              allStates.push(...response.data.data);
              successCount++;
            }
          }

          setCombinedStatesProvinces(allStates);

          // Only filter selected provinces when ALL countries fetched successfully.
          // If any fetch failed, allStates is incomplete and we'd wrongly remove
          // provinces that belong to countries whose fetch didn't return data.
          if (successCount === countryCode.length && selectedProvinces && selectedProvinces.length > 0) {
            const validStateNames = allStates.map(state => state.name);
            const filteredProvinces = selectedProvinces.filter(province =>
              validStateNames.includes(province)
            );

            // Only update if the filtered list is different
            if (filteredProvinces.length !== selectedProvinces.length) {
              setSelectedProvinces(filteredProvinces);
              form.setFieldsValue({ filter_state_province: filteredProvinces });
            }
          }
        } catch (err) {
          console.error('Error fetching states for countries:', err);
          setCombinedStatesProvinces([]);
        }
      } else {
        // Clear combined states if no countries selected or filter disabled
        setCombinedStatesProvinces([]);
      }
    };

    fetchStatesForAllCountries();
  }, [countryCode, isFilterLocation, token]);

  useEffect(() => {
    if (alertMessageType === 'success') {
      setModal({
        open: false,
        type: '',
      });
    }
  }, [alertMessageType]);

  // Fetch products data call when charcters length > 2
  useEffect(() => {
    if (searchQuery.length > 2) {
      fetchData();
    }
  }, [searchQuery]);

  useEffect(() => {
    // Attach event listener for body click
    document.body.addEventListener('click', handleBodyClick);

    // Cleanup event listener on component unmount
    return () => {
      document.body.removeEventListener('click', handleBodyClick);
    };
  }, []);

  // Fetch products on search by name from DB
  const fetchData = async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          authorization: `Bearer ${token}`,
        },
        params: {
          search: searchQuery,
        },
      };
      const response = await axios.get(
        `${process.env.REACT_APP_ENITURE_API_URL}/get_shipping_rule_products`,
        config
      );

      setData((prevData) => [...response.data.data]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const hanldeModalToggling = useCallback(
    (open = false, type = '') =>
      setModal({
        open,
        type,
      }),
    []
  );

  const filterOptionsBrands = (input, option) => {
    return (
      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 &&
      !selectedBrands.includes(option.key)
    );
  };

  const filterServices = (input, option) => {
    return (
      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 &&
      !selectedServices.includes(option.key)
    );
  };

  const handleChangeServices = (selectedValues) => {
    setSelectedServices(selectedValues);
  };

  const filterOptionsCategories = (input, option) => {
    return (
      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 &&
      !selectedCategories.includes(option.key)
    );
  };

  const handleChangeBrands = (selectedValues) => {
    setSelectedBrands(selectedValues);
  };

  const handleChangeProducts = (selectedValues) => {
    setData([]);
    setSearchQuery('');
    setSelectedProducts(selectedValues);
  };

  const handleSearch = (value) => {
    setData([]);
    setSearchQuery(value);
  };

  // Click outside the dropdown, clear data
  const handleBodyClick = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setData([]);
      setSearchQuery('');
    }
  };

  const handleChangeCategories = (selectedValues) => {
    setSelectedCategories(selectedValues);
  };

  const handleChangeProvinces = (selectedValues) => {
    setSelectedProvinces(selectedValues);
  };

  // Handler for main Countries dropdown (single selection)
  const handleChangeCountry = (value) => {
    form.resetFields(['filter_state_province']);
    setMainCountry(value);
    setLatestCountry(value || 'US');
  };

  // Handler for Filter by Location Countries dropdown (multiple selection)
  const handleChangeCountryLocation = (value) => {
    setCountryCode(value);

    // Track the latest selected country for state/province fetching
    // The last item in the array is always the most recently selected
    if (value && value.length > 0) {
      setLatestCountry(value[value.length - 1]);
    } else {
      // If all countries are cleared, default to US
      setLatestCountry('US');
    }
  };

  const handleChangeApplyRuleTo = (value) => {
    form.resetFields(['filter_categories', 'filter_brands', 'filter_products']);
    setApplyRuleTo(value)
  };

  const handleProviderServices = (value, rule_type = '') => {
    // value is a single value (bare slug or composite "slug:installed_carrier_id") for
    // Show Methods/Override Rates/Ground Transit, or an array of them for Hide Methods'
    // multi-select Provider field.
    const values = Array.isArray(value) ? value : [value].filter(Boolean);
    setIsLTL(values.some((v) => String(v).includes('ltl')));
    setCarrierSlug(value);
    // Hide Methods' Provider field is additive (multi-select) - adding/removing a provider
    // should only refresh which Service options are AVAILABLE, not wipe out services the
    // merchant already picked. Other rule types still use a single-select Provider field,
    // where switching providers genuinely means a different service vocabulary, so the old
    // selection is cleared as before.
    const isHideMethods = ruleType == 2 || rule_type == 2;
    if (!isHideMethods) {
      setSelectedServices([]);
      form.resetFields(['filter_services']);
    }
    if (ruleType == 6 || rule_type == 6 || isHideMethods) {
      getCarrierServices(value)
    }
  };

  const getCarrierServices = async (value) => {
    // value may be a single value (bare slug or composite "slug:installed_carrier_id") or
    // an array of them (Hide Methods' multi-select Provider field) - merge the enabled
    // accessorials, and the named parcel services, across every selected connection so the
    // Service dropdown offers anything enabled on ANY of them.
    const values = Array.isArray(value) ? value : [value].filter(Boolean);
    const accessorialResultsByProvider = [];
    const parcelServiceResultsByProvider = [];
    await Promise.all(
      values.map((singleValue) => {
        const [slug, instanceId] = String(singleValue).split(':');
        const carrier = installedCarriers?.find((c) =>
          instanceId ? String(c?.id) === instanceId : c?.slug == slug
        );
        if (!carrier) {
          return Promise.resolve();
        }
        return dispatch(getCarrServices(
          carrier?.id,
          token,
          carrier?.carrier_type,
          slug,
          (result) => {
            accessorialResultsByProvider.push(result || []);
          },
          (result) => {
            // LTL carriers return a single pseudo-entry here (e.g. "ABF Freight LTL
            // Freight") meant for Override Rates' base-rate concept, not a real named
            // service - only parcel carriers have a genuine named-services list.
            if (carrier?.carrier_type === 2) {
              parcelServiceResultsByProvider.push(result || []);
            }
          }
        ))
      })
    );
    const mergedBySac = {};
    accessorialResultsByProvider.flat().forEach((accessorial) => {
      if (accessorial?.status) {
        mergedBySac[accessorial.accessorial_service_sac] = accessorial;
      }
    });
    setAccessorials(Object.values(mergedBySac));

    const mergedServicesByValue = {};
    parcelServiceResultsByProvider.flat().forEach((service) => {
      if (service?.value) {
        mergedServicesByValue[service.value] = service;
      }
    });
    setCarrierServicesLocal(Object.values(mergedServicesByValue));
  }

  const handleChangeWarehouses = (selectedValues) => {
    setSelectedWarehouses(selectedValues);
  };

  const handleChangeRule = (selectedRule) => {
    setApplyTo(selectedRule.target.value);
  };

  const handleSelectChange = (selectedValues) => {
    setSelectedPostalCodes(selectedValues);
  };

  const showMoreItems = (key) => {
    setRecordId(key);
  };

  // carrierSlug is a single value for Show Methods/Override Rates/Ground Transit, or an
  // array for Hide Methods' multi-select Provider field - an empty array is still truthy
  // in JS, so a plain !carrierSlug check alone would wrongly treat "nothing selected" as
  // "something selected" for the array case.
  const hasCarrierSelected = Array.isArray(carrierSlug) ? carrierSlug.length > 0 : !!carrierSlug;

  // Single-value Provider fields (Show Methods, Override Rates, Hide Delivery Estimates, Ground
  // Transit) save only the carrier slug. If the saved carrier has since been deactivated, keep
  // exactly ONE inactive connection with that slug in the list so the field shows its name
  // instead of the raw slug - and only when no active connection with that slug exists, so other
  // inactive connections of the same type stay hidden.
  const watchedProvider = Form.useWatch('filter_provider', form);
  const isProviderOption = (carrier) => {
    if (carrier.is_enabled === 1) return true;
    if (typeof watchedProvider !== 'string' || carrier.slug !== watchedProvider) return false;
    const hasActiveWithSlug = installedCarriers?.some((c) => c.slug === watchedProvider && c.is_enabled === 1);
    if (hasActiveWithSlug) return false;
    return installedCarriers?.find((c) => c.slug === watchedProvider)?.id === carrier.id;
  };
  // Hide Methods can mix LTL and parcel providers in one rule (multi-select) - the Service
  // dropdown needs to offer BOTH vocabularies (LTL accessorials + parcel named services)
  // together in that case, rather than picking just one based on a single isLTL flag.
  const selectedProviderCarrierTypes = (Array.isArray(carrierSlug) ? carrierSlug : [carrierSlug].filter(Boolean))
    .map((value) => {
      const [slug, instanceId] = String(value).split(':');
      const carrier = installedCarriers?.find((c) =>
        instanceId ? String(c?.id) === instanceId : c?.slug == slug
      );
      return carrier?.carrier_type;
    });
  const hasLtlProvider = selectedProviderCarrierTypes.includes(1);
  const hasParcelProvider = selectedProviderCarrierTypes.includes(2);
  // Hide Methods has no LTL services: selecting an LTL provider hides the whole provider.
  // Override Rates shares this dropdown and still uses the LTL options.
  const showLtlServiceOptions = hasLtlProvider && ruleType != 2;

  // Use combined states from all selected countries for Filter by Location, otherwise use regular states
  // Check both state variable and form field value to handle modal opening edge case
  const currentCountries = countryCode?.length > 0 ? countryCode : (form.getFieldValue('filter_country') || []);
  const useFilterLocationStates = isFilterLocation && currentCountries.length > 0;

  const statesProvince = (useFilterLocationStates && combinedStatesProvinces.length > 0
    ? combinedStatesProvinces
    : statesProvinces)?.map((item) => ({
      key: item.code,
      value: item.name,
    }));

  const filterOptionsProvinces = (input, option) => {
    return (
      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 &&
      !selectedProvinces.includes(option.key)
    );
  };

  const filterOptionsWarehouses = (input, option) => {
    return (
      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 &&
      !selectedWarehouses.includes(option.key)
    );
  };

  const editLocation = useCallback(
    (record) => {
      console.log('Record data being set in form:', record);
      hanldeModalToggling(true, 'edit');
      setShippingRuleId(record.uuid);
      // If the saved provider is an instore/local-delivery key that is no longer enabled,
      // drop it so the field shows empty instead of the raw key string. Hide Methods rules
      // store filter_provider as an array (multi-select); other rule types still store a
      // single value - handle both shapes.
      const savedProvider = record.filter_provider ?? '';
      const validWarehouseValues = new Set(warehouseProviderOptions.map(opt => opt.value));
      const dropInvalidWarehouseValue = (value) => {
        const isWarehouseProvider = typeof value === 'string' && (value.startsWith('instore_') || value.startsWith('local_delivery_'));
        return isWarehouseProvider && !validWarehouseValues.has(value) ? null : value;
      };
      const resolvedProvider = Array.isArray(savedProvider)
        ? savedProvider.map(dropInvalidWarehouseValue).filter((value) => value !== null)
        : (dropInvalidWarehouseValue(savedProvider) ?? undefined);
      form.setFieldsValue({ ...record, filter_provider: resolvedProvider });
    },
    [form, hanldeModalToggling, warehouseProviderOptions]
  );

  const openDeleteLocationModal = useCallback(
    (record) => {
      hanldeModalToggling(true, 'delete');
      setShippingRuleId(record.uuid);
    },
    [hanldeModalToggling]
  );

  const isAvailable = async (uuid, available) => {
    try {
      dispatch(dispatchAlert(true, 'loading'));
      const url = `${process.env.REACT_APP_ENITURE_API_URL}/updateAvaiableStatus`,
        config = {
          headers: {
            authorization: `Bearer ${token}`,
          },
        };
      const reqData = { uuid: uuid, available: available };
      const {
        data: { error, data, message },
      } = await axios.post(url, reqData, config);

      if (!data.error) {
        dispatch({
          type: types.UPDATE_SHIPPING_RULE,
          payload: data.shippingRule,
        });
      }
      dispatch(dispatchAlert(true, error ? 'error' : 'success', message));
    } catch (err) {
      dispatch(dispatchAlert(false, null));
    }
  };
  // 
  const updateFormFields = async (text) => {
    // Handle filter_country - main dropdown uses single value, Filter by Location uses array
    if (text?.filter_country) {
      // Parse if it's a JSON string
      let parsedCountry = text.filter_country;
      if (typeof text.filter_country === 'string' && text.filter_country.startsWith('[')) {
        try {
          parsedCountry = JSON.parse(text.filter_country);
        } catch (e) {
          console.error('Error parsing filter_country:', e);
        }
      }

      // For Filter by Location rules (1, 2, 6, 8, 10), store as array
      if (text?.rule_type == 1 || text?.rule_type == 2 || text?.rule_type == 6 || text?.rule_type == 8 || text?.rule_type == 10) {
        const countryArray = Array.isArray(parsedCountry) ? parsedCountry : [parsedCountry];
        setCountryCode(countryArray);
        setLatestCountry(countryArray[countryArray.length - 1] || 'US');
        text.filter_country = countryArray; // Update text object with parsed array
      } else {
        // For main country dropdown, store as single string
        const country = Array.isArray(parsedCountry) ? parsedCountry[0] : parsedCountry;
        setMainCountry(country || 'US');
        setLatestCountry(country || 'US');
      }
    } else {
      setMainCountry('US');
      setCountryCode([]);
      setLatestCountry('US');
      text.filter_country = [];
    }
    if (text?.rule_type == 11) {
      let applyToProviders = 'ltl_and_parcel';
      if (text?.filter_settings) {
        try {
          const fs = JSON.parse(text.filter_settings);
          applyToProviders = fs.apply_to_providers ?? 'ltl_and_parcel';
        } catch (e) {}
      }
      setApplyTo(applyToProviders);
    } else if (text?.rule_type == 6 || text?.rule_type == 2) {
      handleProviderServices(text?.filter_provider, text?.rule_type);
      setApplyTo(Number(text?.apply_to ?? 1));
    } else {
      setApplyTo(Number(text?.apply_to ?? 1));
    }

    if (text?.filter_country == undefined || text?.filter_country == '' || (Array.isArray(text?.filter_country) && text?.filter_country.length === 0)) {
      setMainCountry('US');
      setCountryCode([]);
      setLatestCountry('US');
      text.filter_country = [];
      text.filter_state_province = [];
    }
    setAvailable(text?.available);
    setRuleType(text?.rule_type);

    // For rule types with multiple filter support (1, 2, 3, 4, 5, 7, 10), load all filter types
    if (text?.rule_type == 1 || text?.rule_type == 2 || text?.rule_type == 3 || text?.rule_type == 4 || text?.rule_type == 5 || text?.rule_type == 7 || text?.rule_type == 10 || text?.rule_type == 12) {
      // Map all three filter types if they exist
      if (text?.categories) text.filter_categories = text.categories;
      if (text?.brands) text.filter_brands = text.brands;
      if (text?.products) text.filter_products = text.products;
    } else {
      // For other rule types, use the old apply_rule_to logic
      if (text?.apply_rule_to == 1) {
        text.filter_categories = text?.categories;
      } else if (text?.apply_rule_to == 2) {
        text.filter_brands = text?.brands;
      } else if (text?.apply_rule_to == 3) {
        text.filter_products = text?.products;
      }
    }
    setApplyRuleTo(text?.apply_rule_to ?? 1);

    // Set filter logic from either filter_logic or is_all_filter_applied
    if (text?.filter_logic) {
      setFilterLogic(text.filter_logic);
    } else if (text?.is_all_filter_applied !== undefined) {
      setFilterLogic(text.is_all_filter_applied == 1 ? 'all' : 'any');
    } else {
      setFilterLogic('all');
    }

    // Special handling for rule types 1, 2, 3, 4, 5, 6, 7, 8, 10, and 13 that use filter_settings
    if ((text?.rule_type == 1 || text?.rule_type == 2 || text?.rule_type == 3 || text?.rule_type == 4 || text?.rule_type == 5 || text?.rule_type == 6 || text?.rule_type == 7 || text?.rule_type == 8 || text?.rule_type == 10 || text?.rule_type == 13) && text?.filter_settings) {
      try {
        const filterSettings = JSON.parse(text.filter_settings);

        text.label_as = filterSettings.label_as || text.label_as || '';
        // Extract data from filter_settings
        let filterCountry = filterSettings.filter_country || text.filter_country;

        // Parse if it's a JSON string
        if (typeof filterCountry === 'string' && filterCountry.startsWith('[')) {
          try {
            filterCountry = JSON.parse(filterCountry);
          } catch (e) {
            console.error('Error parsing filter_country from filter_settings:', e);
          }
        }

        text.filter_country = filterCountry;

        // Parse filter_state_province if it's a JSON string
        let filterStateProvince = filterSettings.filter_state_province || [];
        if (typeof filterStateProvince === 'string' && filterStateProvince.startsWith('[')) {
          try {
            filterStateProvince = JSON.parse(filterStateProvince);
          } catch (e) {
            console.error('Error parsing filter_state_province from filter_settings:', e);
          }
        }
        text.filter_state_province = filterStateProvince;

        // Also update the state variables - handle array or string for backwards compatibility
        const country = filterCountry || [];
        // For Filter by Location rules (1, 2, 6, 8, 10), use countryCode array
        if (text?.rule_type == 1 || text?.rule_type == 2 || text?.rule_type == 6 || text?.rule_type == 8 || text?.rule_type == 10) {
          const countryArray = Array.isArray(country) ? country : [country];
          setCountryCode(countryArray);
          setLatestCountry(countryArray[countryArray.length - 1] || 'US');
        } else {
          // For main country dropdown, use single string
          const singleCountry = Array.isArray(country) ? country[0] : country;
          setMainCountry(singleCountry || 'US');
          setLatestCountry(singleCountry || 'US');
        }
        setSelectedProvinces(Array.isArray(filterStateProvince) ? filterStateProvince : []);

        // If states exist but country is missing, keep location filter enabled
        if (
          filterStateProvince?.length > 0 &&
          (!text?.filter_country || text.filter_country.length === 0)
        ) {
          setIsFilterLocation(true);
        }


        // Update filter fields from filter_settings
        setIsFilterWeight(filterSettings.isFilterWeight || false);
        setIsFilterPrice(filterSettings.isFilterPrice || false);
        setIsFilterQuantity(filterSettings.isFilterQuantity || false);
        setIsFilterCategory(filterSettings.isFilterCategory || false);
        setIsFilterBrand(filterSettings.isFilterBrand || false);
        setIsFilterProduct(filterSettings.isFilterProduct || false);
        setIsFilterLocation(filterSettings.isFilterLocation || filterSettings.isLocationFilter || false);
        setIsFilterMaxShippingRate(filterSettings.isFilterMaxShippingRate || false);

        // Set weight fields - handle both camelCase and snake_case from backend
        text.isFilterWeight = filterSettings.isFilterWeight || false;
        text.weight_from = filterSettings.weight_from || filterSettings.weightFrom || '';
        text.weight_to = filterSettings.weight_to || filterSettings.weightTo || '';

        // Set price fields - handle both camelCase and snake_case from backend
        text.isFilterPrice = filterSettings.isFilterPrice || false;
        text.price_from = filterSettings.price_from || filterSettings.priceFrom || '';
        text.price_to = filterSettings.price_to || filterSettings.priceTo || '';        
        text.priceBasis = filterSettings.priceBasis || 'regular';

        // Set quantity fields - handle both camelCase and snake_case from backend
        text.isFilterQuantity = filterSettings.isFilterQuantity || false;
        text.quantity_from = filterSettings.quantity_from || filterSettings.quantityFrom || '';
        text.quantity_to = filterSettings.quantity_to || filterSettings.quantityTo || '';

        // Set max shipping rate fields (for ruleType 6)
        if (text?.rule_type == 6) {
          text.isFilterMaxShippingRate = filterSettings.isFilterMaxShippingRate || false;
          text.filter_max_shipping_rate = filterSettings.filter_max_shipping_rate || filterSettings.filterMaxShippingRate || '';

          // Set service rates for override rule
          text.service_rates = filterSettings.service_rates || filterSettings.serviceRates || '';
        }

        // Set filter services and update selectedServices state (Hide Methods, Override Rates)
        if (text?.rule_type == 2 || text?.rule_type == 6) {
          let filterServices = filterSettings.filter_services || filterSettings.filterServices || [];
          if (text?.rule_type == 2) {
            // Hide Methods no longer has LTL services (an LTL provider is hidden whole), so drop
            // LTL keys saved on older rules instead of showing them as raw tags.
            const ltlServiceKeys = ['transportation', 'residential', 'liftgate', 'notify', 'limitedAccess', 'insideDelivery', 'twoMan', 'appointment'];
            filterServices = filterServices.filter((service) => !ltlServiceKeys.includes(service));
          }
          text.filter_services = filterServices;
          setSelectedServices(filterServices);
        }

        // Set filter selected values for filters
        text.isFilterCategory = filterSettings.isFilterCategory || false;
        text.filter_categories = filterSettings.filter_categories || [];
        setSelectedCategories(filterSettings.isFilterCategory ? (filterSettings.filter_categories || []) : []);

        text.isFilterBrand = filterSettings.isFilterBrand || false;
        text.filter_brands = filterSettings.filter_brands || [];
        setSelectedBrands(filterSettings.isFilterBrand ? (filterSettings.filter_brands || []) : []);

        text.isFilterProduct = filterSettings.isFilterProduct || false;
        const filterProducts = filterSettings.filter_products || [];
        // Products use labelInValue format: {key: id, label: name}
        text.filter_products = Array.isArray(filterProducts) && filterProducts.length > 0 && typeof filterProducts[0] === 'object'
          ? filterProducts
          : filterProducts.map(id => ({ key: id, label: id }));
        setSelectedProducts(filterSettings.isFilterProduct ? text.filter_products : []);

        // Set location filter - handle both isFilterLocation and isLocationFilter from backend
        text.isFilterLocation = filterSettings.isFilterLocation || filterSettings.isLocationFilter || false;


        // Set address type filter
        setIsAddressType(filterSettings.isAddressType === 1 || filterSettings.isAddressType === true);
        setSelectedAddressType(filterSettings.selected_address_type ?? 0);
        text.isAddressType = filterSettings.isAddressType || 0;
        text.selected_address_type = filterSettings.selected_address_type ?? 0;

        // Set customer group filter
        setIsFilterCustomerGroup(filterSettings.isFilterCustomerGroup || false);
        const cGroups = filterSettings.filter_customer_groups || [];
        setSelectedCustomerGroups(cGroups);
        text.isFilterCustomerGroup = filterSettings.isFilterCustomerGroup || false;
        text.filter_customer_groups = cGroups;
        // Respect RAD availability: if RAD is suspended/not installed, reset to default (All)
        const isRadInstalled = radPlans?.currentPackage?.status === 1 || radPlans?.currentPackage?.status === '1';
        // RAD is enabled if installed AND settings don't force always_quote_residential_delivery
        // Default to enabled when RAD is installed but settings aren't loaded yet (new store case)
        let radEnabled = false;
        if (isRadInstalled) {
          if (radSettings?.settings) {
            try {
              const settings = JSON.parse(radSettings.settings);
              radEnabled = !settings?.always_quote_residential_delivery;
            } catch (e) {
              // If parsing fails, default to enabled when RAD is installed
              radEnabled = true;
            }
          } else {
            // Settings not loaded yet or don't exist - default to enabled when RAD is installed
            radEnabled = true;
          }
        }
        let savedSelectedAddressType = filterSettings.selected_address_type ?? 0;
        let savedIsAddressTypeFlag = filterSettings.isAddressType === 1 || filterSettings.isAddressType === true;

        if (!(radEnabled && isRadInstalled)) {
          // RAD unavailable => clear any previously saved non-zero selection so UI shows "All address types"
          savedSelectedAddressType = 0;
          // Keep the checkbox state - user can still filter by "All address types" when RAD is disabled
        }

        setIsAddressType(savedIsAddressTypeFlag);
        setSelectedAddressType(savedSelectedAddressType);
        text.isAddressType = savedIsAddressTypeFlag ? 1 : 0;
        text.selected_address_type = savedSelectedAddressType;

      } catch (error) {
        console.error('Error parsing filter_settings:', error);
      }
    } else {
      // For non-ruleType 6, use the existing logic
      setIsFilterWeight(text?.isFilterWeight || false);
      setIsFilterPrice(text?.isFilterPrice || false);
      setIsFilterQuantity(text?.isFilterQuantity || false);
      setIsFilterFlatPrice(text?.isFilterFlatPrice || false);
      setIsFilterCategory(text?.isFilterCategory || false);
      setIsFilterBrand(text?.isFilterBrand || false);
      setIsFilterProduct(text?.isFilterProduct || false);
      setIsFilterLocation(text?.isFilterLocation || false);
      setIsFilterMaxShippingRate(text?.isFilterMaxShippingRate || false);
      setIsAddressType(text?.isAddressType === 1 || text?.isAddressType === true);
      setSelectedAddressType(text?.selected_address_type ?? 0);
      setIsFilterCustomerGroup(text?.isFilterCustomerGroup || false);
      setSelectedCustomerGroups(text?.filter_customer_groups || []);

      let country = text?.filter_country || [];

      // Parse if it's a JSON string
      if (typeof country === 'string' && country.startsWith('[')) {
        try {
          country = JSON.parse(country);
          text.filter_country = country; // Update text object with parsed array
        } catch (e) {
          console.error('Error parsing filter_country:', e);
        }
      }

      // For Filter by Location rules (1, 2, 6, 8, 10), use countryCode array
      if (text?.rule_type == 1 || text?.rule_type == 2 || text?.rule_type == 6 || text?.rule_type == 8 || text?.rule_type == 10) {
        const countryArray = Array.isArray(country) ? country : [country];
        setCountryCode(countryArray);
        setLatestCountry(countryArray[countryArray.length - 1] || 'US');
      } else {
        // For main country dropdown, use single string
        const singleCountry = Array.isArray(country) ? country[0] : country;
        setMainCountry(singleCountry || 'US');
        setLatestCountry(singleCountry || 'US');
      }

      // Parse filter_state_province if it's a JSON string
      let stateProvince = text?.filter_state_province || [];
      if (typeof stateProvince === 'string' && stateProvince.startsWith('[')) {
        try {
          stateProvince = JSON.parse(stateProvince);
          text.filter_state_province = stateProvince;
        } catch (e) {
          console.error('Error parsing filter_state_province:', e);
        }
      }
      setSelectedProvinces(Array.isArray(stateProvince) ? stateProvince : []);

      // Set selected values for filter dropdowns
      // For rule types 1, 2, 3, 4, 5, 7, 10, load filter data even without isFilter flags
      const shouldLoadFilters = text?.rule_type == 1 || text?.rule_type == 2 || text?.rule_type == 3 || text?.rule_type == 4 || text?.rule_type == 5 || text?.rule_type == 7 || text?.rule_type == 10;

      if ((shouldLoadFilters || text?.isFilterCategory) && text?.filter_categories) {
        setSelectedCategories(text.filter_categories);
        if (shouldLoadFilters && !text?.isFilterCategory) {
          setIsFilterCategory(true);
        }
      } else {
        setSelectedCategories([]);
      }

      if ((shouldLoadFilters || text?.isFilterBrand) && text?.filter_brands) {
        setSelectedBrands(text.filter_brands);
        if (shouldLoadFilters && !text?.isFilterBrand) {
          setIsFilterBrand(true);
        }
      } else {
        setSelectedBrands([]);
      }

      if ((shouldLoadFilters || text?.isFilterProduct) && text?.filter_products) {
        // Products use labelInValue format: {key: id, label: name}
        const filterProducts = Array.isArray(text.filter_products) && text.filter_products.length > 0 && typeof text.filter_products[0] === 'object'
          ? text.filter_products
          : text.filter_products.map(id => ({ key: id, label: id }));
        text.filter_products = filterProducts;
        setSelectedProducts(filterProducts);
        if (shouldLoadFilters && !text?.isFilterProduct) {
          setIsFilterProduct(true);
        }
      } else {
        text.filter_products = [];
        setSelectedProducts([]);
      }
    }


    // ---------------


    editLocation(text);
  };
  const helptext = ruleType == 6 ? 'Shipment' : 'Cart';

  const onFinish = useCallback(
    (values) => {
      values = {
        ...values,
        apply_to: applyTo,
        available: available,
      };

      // Add filter_logic and is_all_filter_applied only for rule types that support AND/OR logic
      if (values['rule_type'] == 1 || values['rule_type'] == 2 || values['rule_type'] == 3 || values['rule_type'] == 4 || values['rule_type'] == 5 || values['rule_type'] == 6 || values['rule_type'] == 7 || values['rule_type'] == 8 || values['rule_type'] == 10) {
        values = {
          ...values,
          filter_logic: filterLogic,
          is_all_filter_applied: filterLogic === 'all' ? 1 : 0,
          isFilterWeight: isFilterWeight,
          isFilterPrice: isFilterPrice,
          isFilterQuantity: isFilterQuantity,
          isFilterCategory: isFilterCategory,
          isFilterBrand: isFilterBrand,
          isFilterProduct: isFilterProduct,
          isFilterLocation: isFilterLocation,
          isLocationFilter: isFilterLocation,
        };
      }

      // Add filter flags and logic for Ground Transit Restriction
      if (values['rule_type'] == '12') {
        values = {
          ...values,
          filter_logic: filterLogic,
          is_all_filter_applied: filterLogic === 'all' ? 1 : 0,
          isFilterCategory: isFilterCategory,
          isFilterBrand: isFilterBrand,
          isFilterProduct: isFilterProduct,
        };
      }

      // Add address type filter for applicable rule types
      if (values['rule_type'] == 1 || values['rule_type'] == 2 || values['rule_type'] == 6 || values['rule_type'] == 7 || values['rule_type'] == 8 || values['rule_type'] == 10) {
        values = {
          ...values,
          isAddressType: isAddressType,
          // PO/APO/DPO/FPO (3) is supported by Hide Methods only
          selected_address_type: (values['rule_type'] != 2 && selectedAddressType == 3) ? 0 : selectedAddressType,
        };
      }

      // Add customer group filter for applicable rule types (all except 9, 11, 12, 13)
      if (values['rule_type'] != 9 && values['rule_type'] != 11 && values['rule_type'] != '12' && values['rule_type'] != 13 && values['rule_type'] != 14) {
        values = {
          ...values,
          isFilterCustomerGroup: isFilterCustomerGroup,
          filter_customer_groups: selectedCustomerGroups,
        };
      }

      if (values['rule_type'] == 6) {
        values = {
          ...values,
          isFilterMaxShippingRate: isFilterMaxShippingRate,
          filterMaxShippingRate: values['filter_max_shipping_rate'] || '',
        };
      }

      if (values['rule_type'] == 13) {
        values = {
          ...values,
          isFilterWeight: isFilterWeight,
        };
      }

      if (values['rule_type'] == 11) {
        const cheapestRateMap = {
          'ltl': 1,
          'parcel': 2,
          'ltl_and_parcel': 3,
          'cheapest_ltl_or_parcel': 4,
        };
        values = {
          ...values,
          apply_to_providers: applyTo,
          cheapest_rate_for_carriers: cheapestRateMap[applyTo] ?? 3,
        };
      }

      if (values['rule_type'] == 10) {
        values = {
          ...values,
          isFilterFlatPrice: isFilterFlatPrice,
          isFilterLocation: isFilterLocation,
          isLocationFilter: isFilterLocation,
        };
      }

      let error = false,
        errormsg = '',
        data = {};

      if (modal.open && modal.type === 'edit') {
        data = shippingRules?.find((sg) => sg.uuid === shippingRuleId) ?? {};
      }

      if (
        values['isFilterWeight'] &&
        parseFloat(values['weight_to']) <= parseFloat(values['weight_from'])
      ) {
        error = true;
        errormsg = 'From weight cannot be greater than or equal to To weight.';
      } else if (
        values['isFilterPrice'] &&
        parseFloat(values['price_to']) <= parseFloat(values['price_from'])
      ) {
        error = true;
        errormsg = 'From Price cannot be greater than or equal to To Price.';
      } else if (
        values['isFilterQuantity'] &&
        parseFloat(values['quantity_to']) <= parseFloat(values['quantity_from'])
      ) {
        error = true;
        errormsg =
          'From Quantity cannot be greater than or equal to To Quantity.';
      } else if (values['rule_type'] == 11) {
        const enabledCarriers = installedCarriers?.filter((c) => c.is_enabled === 1) ?? [];
        const enabledLtl = enabledCarriers.filter((c) => c.carrier_type === 1);
        const enabledParcel = enabledCarriers.filter((c) => c.carrier_type === 2);

        if ((applyTo === 'ltl' || applyTo === 'parcel' ||applyTo === 'ltl_and_parcel' || applyTo === 'cheapest_ltl_or_parcel') && (enabledLtl.length + enabledParcel.length) < 2) {
          error = true;
          errormsg = 'You must have at least two carriers enabled to add the cheapest rates rule.';
        }
      }

      if (error) {
        dispatch({
          type: 'ALERT_MESSAGE',
          payload: {
            showAlertMessage: false,
            alertMessageType: 'loading',
          },
        });
        dispatch({
          type: 'ALERT_MESSAGE',
          payload: {
            alertMessage: errormsg,
            showAlertMessage: true,
            alertMessageType: 'error',
          },
        });
      } else {
        // Ensure filter_logic and is_all_filter_applied are added for applicable rule types
        const ruleTypeNum = parseInt(values['rule_type']);
        if ([1, 2, 3, 4, 5, 6, 7, 8, 10, 12].includes(ruleTypeNum)) {
          values.filter_logic = filterLogic;
          values.is_all_filter_applied = filterLogic === 'all' ? 1 : 0;
        }

        console.log('Shipping Rule Payload:', { ...data, ...values });
        console.log('Rule Type:', values['rule_type'], 'Type:', typeof values['rule_type']);
        console.log('Filter Logic:', filterLogic);
        console.log('is_all_filter_applied:', values.is_all_filter_applied);
        dispatch(saveShippingRule({ ...data, ...values }, token));
        setSelectedCategories([]);
        setSelectedProducts([]);
        setSelectedBrands([]);
        setIsFilterCustomerGroup(false);
        setSelectedCustomerGroups([]);
      }
    },
    [
      dispatch,
      form,
      modal.open,
      modal.type,
      shippingRuleId,
      shippingRules,
      token,
      available,
      isFilterWeight,
      isFilterPrice,
      isFilterQuantity,
      applyTo,
      isFilterFlatPrice,
      isFilterCategory,
      isFilterBrand,
      isFilterProduct,
      isFilterLocation,
      isFilterMaxShippingRate,
      filterLogic,
      isAddressType,
      selectedAddressType,
      isFilterCustomerGroup,
      selectedCustomerGroups,
    ]
  );

  const columns = [
    {
      key: 'rule_name',
      title: 'Rule Name',
      dataIndex: 'rule_name',
    },
    {
      key: 'rule_type',
      title: 'Type',
      dataIndex: 'rule_type',
      render: (rule_type) => (
        <Space size='small'>{shippingRuleTypes[rule_type]}</Space>
      ),
    },

    {
      key: 'available',
      title: 'Available',
      dataIndex: 'available',
      render: (available, record) => (
        <Space size='small'>
          <a
            href='#!'
            onClick={() => {
              if (record.rule_type == 11 && !record.available) {
                const enabledCarriers = installedCarriers?.filter((c) => c.is_enabled === 1) ?? [];
                const enabledLtl = enabledCarriers.filter((c) => c.carrier_type === 1);
                const enabledParcel = enabledCarriers.filter((c) => c.carrier_type === 2);
                let applyToProviders = 'ltl_and_parcel';
                if (record.filter_settings) {
                  try {
                    const fs = JSON.parse(record.filter_settings);
                    applyToProviders = fs.apply_to_providers ?? 'ltl_and_parcel';
                  } catch (e) {}
                }
                let validationError = false;
                if (applyToProviders === 'ltl' && enabledLtl.length < 2) {
                  validationError = true;
                } else if (applyToProviders === 'parcel' && enabledParcel.length < 2) {
                  validationError = true;
                } else if ((applyToProviders === 'ltl_and_parcel' || applyToProviders === 'cheapest_ltl_or_parcel') && (enabledLtl.length < 1 || enabledParcel.length < 1)) {
                  validationError = true;
                }
                if (validationError) {
                  dispatch(dispatchAlert(true, 'error', 'You must have at least two carriers enabled to add the cheapest rates rule.'));
                  return;
                }
              }
              isAvailable(record.uuid, record.available);
            }}
          >
            {available ? 'Yes' : 'No'}
          </a>
        </Space>
      ),
    },
    {
      key: 'action',
      title: 'Action',
      render: (text) => (
        <>
          <Space size='middle'>
            <Button onClick={() => updateFormFields(text)}>Edit</Button>
            <Button
              onClick={() => openDeleteLocationModal(text)}
              className={'btn-danger'}
            >
              Delete
            </Button>
          </Space>
        </>
      ),
    },
  ];

  // ----Skelton---------
  if (!shippingRules) return (
    <Fragment>
      <Space direction='vertical' size={'large'} className={'w-100'}>
        <Row gutter={30}>
          <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
            {/* Header Area */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <Skeleton.Input active style={{ width: 200, height: 38 }} />
              <Skeleton.Button active shape="round" style={{ width: 80, height: 38 }} />
            </div>

            {/* Description Text */}
            <Skeleton
              active
              paragraph={{ rows: 1, width: '65%' }}
              title={false}
              style={{ marginBottom: '24px' }}
            />

            {/* Rules Table Area */}
            <div style={{ border: '1px solid #f0f0f0', borderRadius: '2px', overflow: 'hidden' }}>
              {/* Table Header Row */}
              <div style={{
                display: 'flex',
                gap: '24px',
                padding: '16px',
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <Skeleton.Input active style={{ width: '35%', height: 16 }} />
                <Skeleton.Input active style={{ width: '20%', height: 16 }} />
                <Skeleton.Input active style={{ width: '15%', height: 16 }} />
                <Skeleton.Input active style={{ width: '20%', height: 16 }} />
              </div>

              {/* Table Rows */}
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  style={{
                    display: 'flex',
                    gap: '24px',
                    padding: '16px',
                    borderBottom: item !== 4 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  {/* Rule Name Column */}
                  <div style={{ width: '35%' }}>
                    <Skeleton.Input active style={{ width: '90%', height: 20 }} />
                  </div>

                  {/* Type Column */}
                  <div style={{ width: '20%' }}>
                    <Skeleton.Input active style={{ width: '80%', height: 20 }} />
                  </div>

                  {/* Available Column */}
                  <div style={{ width: '15%' }}>
                    <Skeleton.Input active style={{ width: '40%', height: 20 }} />
                  </div>

                  {/* Action Column */}
                  <div style={{ width: '20%', display: 'flex', gap: '8px' }}>
                    <Skeleton.Button active shape="round" style={{ width: 65, height: 32 }} />
                    <Skeleton.Button active shape="round" style={{ width: 65, height: 32 }} />
                  </div>
                </div>
              ))}

              {/* Pagination Area */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '16px',
                borderTop: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Skeleton.Button active style={{ width: 32, height: 32 }} />
                  <Skeleton.Button active style={{ width: 32, height: 32 }} />
                  <Skeleton.Button active style={{ width: 32, height: 32 }} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Space>
    </Fragment>
  );
  // ----Skelton---------
  return (
    <Fragment>
      <Space direction='vertical' size={'large'} className={'w-100'}>
        <Row gutter={30}>
          <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
            <Title level={4}>
              Shipping Rules{' '}
              <Button
                type='primary'
                onClick={() => {
                  form.setFieldsValue(initialState);
                  setAvailable(true);
                  setRuleType(10);
                  setApplyTo(0);
                  setMainCountry('US');
                  setCountryCode([]);
                  setLatestCountry('US');
                  setIsFilterWeight(false);
                  setIsFilterPrice(false);
                  setIsFilterQuantity(false);
                  setIsFilterFlatPrice(false);
                  setIsFilterCategory(false);
                  setIsFilterBrand(false);
                  setIsFilterProduct(false);
                  setIsFilterLocation(false);
                  setIsFilterMaxShippingRate(false);
                  setIsAddressType(false);
                  setSelectedAddressType(0);
                  setIsFilterCustomerGroup(false);
                  setSelectedCustomerGroups([]);
                  setFilterLogic('all');
                  setCarrierSlug();
                  setCarrierId();
                  setSelectedBrands([]);
                  setSelectedProducts([]);
                  setSelectedCategories([]);
                  setSelectedProvinces([]);
                  setCombinedStatesProvinces([]);
                  hanldeModalToggling(true, 'add');
                  setApplyRuleTo(1);
                  setData([]);
                  setSearchQuery('');
                  setAccessorials([])
                  setIsLTL(false)
                }}
              >
                Add
              </Button>
            </Title>
            <p>
              The Shipping Rules gives you an opportunity to customize the
              behavior of the app.
            </p>
            <Table
              className={'custom-table'}
              dataSource={shippingRules ? addKeysToList(shippingRules) : []}
              columns={columns}
            />
          </Col>
        </Row>
      </Space>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Title className={'mb-0'} level={4}>
            {alertMessageType === 'loading'
              ? 'Loading. Please wait...'
              : 'Shipping Rules'}
          </Title>
        }
        centered
        visible={modal.open && (modal.type === 'add' || modal.type === 'edit')}
        onCancel={() => {
          hanldeModalToggling(false, '');
          form.resetFields();
          setSelectedCategories([]);
          setSelectedBrands([]);
          setSelectedProducts([]);
          setSelectedProvinces([]);
          setCombinedStatesProvinces([]);
          setIsFilterCustomerGroup(false);
          setSelectedCustomerGroups([]);
          dispatch({
            type: 'GET_CARRIER_SERVICES',
            payload: [],
          });
        }}
        destroyOnClose={true}
        footer={null}
        width={800}
      >
        {alertMessageType === 'loading' ? (
          <Skeleton active />
        ) : (
          <Form
            layout='vertical'
            name='add_shipping_rule_info'
            className='form-wrp'
            size={'large'}
            form={form}
            initialValues={initialState}
            onFinish={onFinish}
          >
            <Row gutter={30}>
              <Col
                className='gutter-row'
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
              >
                <Form.Item
                  className={'mb-2'}
                  label='Rule name'
                  name='rule_name'
                  rules={[
                    {
                      required: true,
                      message: 'Rule name is required',
                    },
                  ]}
                >
                  <Input placeholder='Rule name' maxLength={50} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={30}>
              <Col
                className='gutter-row'
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
              >
                <div id='type_dropdown'>
                  <Form.Item
                    className={'mb-2'}
                    label='Type'
                    name='rule_type'
                    rules={[
                      {
                        required: false,
                        message: 'Type',
                      },
                    ]}
                  >
                    <Select
                      placeholder='Type'
                      // onChange={(value) => {
                      //   setRuleType(value);
                      //   value = value == 6 ? setApplyTo(1) : setApplyTo(1);
                      // }}
                      onChange={(value) => {
                        setRuleType(value);
                        // Provider/service values are rule-type specific (Hide Methods stores
                        // "slug:id" lists, other types a bare slug), so a value carried over from
                        // the previous type matches no option and shows as the raw value.
                        form.setFieldsValue({ filter_provider: undefined, filter_services: [] });
                        setCarrierSlug(null);
                        setSelectedServices([]);
                        setAccessorials([]);
                        setCarrierServicesLocal([]);
                        if (value == 10 || value == 2) setApplyTo(0);
                        else if (value == 11) setApplyTo('ltl_and_parcel');
                        else setApplyTo(1);
                      }}

                      getPopupContainer={() =>
                        document.getElementById('type_dropdown')
                      }
                    >
                      <Option value={'14'}>Backup Rates</Option>
                      <Option value={'13'}>Box Sizes Execution</Option>
                      <Option value={'10'}>Flat Shipping Rate</Option>
                      <Option value={'12'}>Ground Transit Time Restrictions</Option>
                      <Option value={'7'}>Hide Delivery Estimates</Option>
                      <Option value={'2'}>Hide Methods</Option>
                      <Option value={'9'}>Large Cart Settings</Option>
                      <Option value={'6'}>Override Rates</Option>
                      <Option value={'5'}>Restrict To Origin Locations</Option>
                      <Option value={'11'}>Show Cheapest Rates</Option>
                      <Option value={'1'}>Show Methods</Option>
                      <Option value={'8'}>Surcharge</Option>
                    </Select>
                  </Form.Item>
                </div>
              </Col>
            </Row>
            {ruleType != 9 && ruleType != 11 && ruleType != 12 && ruleType != 13 && ruleType != 14 ? (
              <Row gutter={30}>
                <Col
                  className='gutter-row'
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                >
                  <Form.Item
                    className={'mb-2'}
                    label='Apply to:'
                    rules={[
                      {
                        required: false,
                        message: 'Apply to',
                      },
                    ]}
                  >
                    {ruleType == 10 ? (
                      <>
                        <Radio
                          value='0'
                          checked={applyTo == 0}
                          defaultChecked
                          onChange={(value) => handleChangeRule(value)}>
                          Shipment
                        </Radio>
                      </>
                    ) : (
                      <>
                        <Radio checked={applyTo == 1} value={1} onChange={handleChangeRule}>Cart</Radio>
                      </>
                    )}

                    {(ruleType == 8 || ruleType == 6 || ruleType == 2) && (
                      <>
                        <Radio
                          checked={applyTo == 0}
                          value='0'
                          onChange={(value) => handleChangeRule(value)}
                        >Shipment</Radio>
                      </>
                    )}

                  </Form.Item>
                </Col>
              </Row>
            ) : null}
            {ruleType == 9 && (
              <LargeCartSettings />
            )}
            {ruleType == 14 && <BackupRateRule />}
            {ruleType == 11 && (
              <Row gutter={30}>
                <Col
                  className='gutter-row'
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                >
                  <Form.Item className={'mb-2'} label='Apply to:'>
                    <Radio.Group
                      value={applyTo}
                      onChange={(e) => setApplyTo(e.target.value)}
                    >
                      <div>
                        <Radio value='ltl'>LTL providers</Radio>
                      </div>
                      <div className='mt-1'>
                        <Radio value='parcel'>Parcel providers</Radio>
                      </div>
                      <div className='mt-1'>
                        <Radio value='ltl_and_parcel'>LTL and parcel providers</Radio>
                      </div>
                      <div className='mt-1'>
                        <Radio value='cheapest_ltl_or_parcel'>Cheapest LTL or parcel</Radio>
                      </div>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            )}
            {ruleType == 13 && (
              <>
                <Row gutter={30}>
                  <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                    <p className='mb-1' style={{ fontSize: '14px' }}>This rule impacts parcel quoting apps only.</p>
                    <p className='mb-2' style={{ fontSize: '14px' }}>
                      Enabling the Box Sizes Execution setting ensures the packaging algorithm runs only when necessary.
                      It will execute only when the cart weight meets the specified conditions, reducing unnecessary processing.
                      Use the settings below to define when the algorithm should be triggered. The algorithm will run if the
                      rule is enabled and the cart weight falls within the range specified in the &lsquo;From&rsquo; and &lsquo;To&rsquo; fields.
                    </p>
                  </Col>
                </Row>
                <Row gutter={30}>
                  <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Title level={5}>Filters</Title>
                  </Col>
                </Row>
                <Row gutter={30}>
                  <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item className={'mb-0'} name='is_filter_weight'>
                      <Checkbox
                        name='is_filter_weight'
                        checked={isFilterWeight}
                        onChange={(e) => setIsFilterWeight(e.target.checked)}
                      >
                        Filter by weight (lbs)
                      </Checkbox>
                      <div className={'text-gray mb-2'}>
                        Enable the checkbox and enter a weight range in lbs; the From field (accepts zero) is required, and leave the To field blank to avoid an upper limit.
                      </div>
                    </Form.Item>
                  </Col>
                  <Col className='gutter-row' xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                      label='From'
                      name='weight_from'
                      rules={[
                        {
                          required: isFilterWeight,
                          message: 'Weight from is required',
                        },
                        {
                          pattern: /^(\d{0,10}(\.\d{0,2})?)?$/,
                          message: 'Please enter up to 10 numbers with 2 decimal places.',
                        },
                      ]}
                    >
                      <Input
                        type='number'
                        onKeyDown={blockInvalidChar}
                        step='0.01'
                        min={0}
                        placeholder='Enter weight from'
                      />
                    </Form.Item>
                  </Col>
                  <Col className='gutter-row' xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                      label='To'
                      name='weight_to'
                      rules={[
                        {
                          required: false,
                          message: 'Weight to is required',
                        },
                        {
                          pattern: /^(\d{0,10}(\.\d{0,2})?)?$/,
                          message: 'Please enter up to 10 numbers with 2 decimal places.',
                        },
                      ]}
                    >
                      <Input
                        type='number'
                        onKeyDown={blockInvalidChar}
                        step='0.01'
                        min={0}
                        placeholder='Enter weight to'
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {ruleType == 12 && (
              <>
                <Row gutter={30}>
                  <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                      className={'mb-2'}
                      label='Number of days'
                      name='number_of_transit_days'
                      rules={[
                        {
                          required: true,
                          message: 'Number of days is required',
                        },
                        {
                          pattern: /^\d+$/,
                          message: 'Please enter a whole number',
                        },
                      ]}
                    >
                      <Input
                        type='number'
                        min={1}
                        step='1'
                        placeholder='Enter number of days to restrict ground service to'
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={30}>
                  <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                      className={'mb-2'}
                      name='ground_metric'
                    >
                      <Radio.Group>
                        <Radio value={0}>Calendar days</Radio>
                        <Radio value={1}>Business days</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={30}>
                  <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                    <div id='ground_transit_provider_dropdown'>
                      <Form.Item
                        className={'mb-2'}
                        label="Shipping provider's nickname"
                        name='filter_provider'
                        rules={[
                          {
                            required: true,
                            message: 'Shipping provider is required',
                          },
                        ]}
                      >
                        <Select
                          placeholder='Select installed providers'
                          allowClear
                          getPopupContainer={() =>
                            document.getElementById('ground_transit_provider_dropdown')
                          }
                        >
                          {installedCarriers?.filter(carrier => carrier.carrier_type == 2 && isProviderOption(carrier)).map((carrier) => (
                            <Option key={carrier?.slug} value={carrier?.slug}>
                              {(carrier.nickname || carrier.name)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </>
            )}
            {(ruleType == 1 ||
              ruleType == 3 ||
              ruleType == 4 ||
              ruleType == 5 || ruleType == 7 || ruleType == 10 ||
              (applyTo == 2 && (ruleType == 8 || ruleType == 6 || ruleType == 2)) || (applyTo == 0 && ruleType == 6) || (applyTo == 1 && ruleType == 6)) && (
                <>
                  {ruleType != 5 && ruleType != 7 && ruleType != 8 && ruleType != 2 && ruleType != 10 && !(ruleType == 6 && !showOverrideRule) && (
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        {/* <div id='country_dropdown'>
                          <Form.Item
                            className={'mb-2'}
                            label='Countriesccc'
                            name='filter_country'
                            rules={[
                              {
                                required: false,
                                message: 'Select Countries',
                              },
                            ]}
                          >
                            <Select
                              placeholder='Select country'
                              value={mainCountry}
                              onChange={(value) => handleChangeCountry(value)}
                              getPopupContainer={() =>
                                document.getElementById('country_dropdown')
                              }
                            >
                              <Option value={'US'}>US</Option>
                              <Option value={'CA'}>CA</Option>
                            </Select>
                          </Form.Item>
                        </div> */}
                      </Col>
                    </Row>
                  )}
                  {(ruleType == 7 || ruleType == 2) && (
                    <>
                      <Row gutter={30}>
                        <Col
                          className='gutter-row'
                          xs={24}
                          sm={24}
                          md={24}
                          lg={24}
                          xl={24}
                        >
                          <div id='provider_dropdown'>
                            <Form.Item
                              className={'mb-2'}
                              label="Shipping provider's nickname"
                              name='filter_provider'
                              rules={[
                                {
                                  required: true,
                                  message: 'Shipping provider is required',
                                },
                              ]}
                            >

                              <Select
                                placeholder='Select installed providers'
                                value={this?.filter_provider || undefined}
                                onChange={handleProviderServices}
                                getPopupContainer={() =>
                                  document.getElementById('provider_dropdown')
                                }
                              >
                                {installedCarriers?.filter(isProviderOption).map((carrier) => (
                                  <Option key={carrier?.slug} value={carrier?.slug}>
                                    {carrier.nickname || carrier.name}
                                  </Option>
                                ))}
                                {ruleType == 2 && warehouseProviderOptions.map((opt) => (
                                  <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                  {(ruleType == 3 || ruleType == 4 || (ruleType == 6 && showOverrideRule)) && (
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <div id='state_dropdown'>
                          <Form.Item
                            className={'mb-0'}
                            label='States/Provinces'
                            name='filter_state_province'
                            rules={[
                              {
                                required: ruleType != 10 && ruleType != 6,
                                message: 'States/Provinces are required',
                              },
                            ]}
                          >
                            <Select
                              mode='multiple'
                              style={{ width: '100%' }}
                              placeholder='Select states/provinces'
                              value={selectedProvinces}
                              allowClear
                              onChange={handleChangeProvinces}
                              filterOption={filterOptionsProvinces}
                              getPopupContainer={() =>
                                document.getElementById('state_dropdown')
                              }
                            >
                              {statesProvince?.map((option) => (
                                <Option key={option.key} value={option.value}>
                                  {option.value}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>
                        {ruleType != 6 && showOverrideRule && (
                          <div className={'text-gray mb-2'}>
                            Only customers from these states/provinces will be presented with shipping rates for this provider.
                          </div>
                        )}
                        {ruleType == 6 && (
                          <div className={'text-gray mb-2'}>
                            The selected states/provinces will determine where this override rate applies.
                            Customers shipping to other states/provinces will continue to see the normal rates.
                          </div>
                        )}
                      </Col>
                    </Row>
                  )}
                  {ruleType == 10 && (
                    <>
                      <Row gutter={30}>
                        <Col
                          className='gutter-row'
                          xs={24}
                          sm={24}
                          md={24}
                          lg={24}
                          xl={24}
                        >
                          <Form.Item
                            className={'mb-2'}
                            label='Label As'
                            name='label_as'
                            rules={[
                              {
                                required: true,
                                message: 'Label As is required',
                              },
                            ]}
                          >
                            <Input placeholder='Label As' maxLength={50} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={30}>
                        <Col
                          className='gutter-row'
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          xl={12}
                        >
                          <Form.Item
                            className={'mb-3'}
                            label='Flat Shipping Rate'
                            name='filter_flat_shipping_rate'
                            rules={[
                              {
                                required: true,
                                message: 'Flat Shipping Rate is required',
                              },
                              {
                                pattern: /^(\d{0,15}(\.\d{0,2})?)?$/,
                                message: 'Please enter up to 15 numbers with 2 decimal places.',
                              },
                            ]}
                          >
                            <Input
                              type='number'
                              onKeyDown={blockInvalidChar}
                              step='0.01'
                              min={0}
                              placeholder='Enter flat shipping rate'
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          className='gutter-row'
                          xs={12}
                          sm={12}
                          md={12}
                          lg={12}
                          xl={12}
                        >
                          <Form.Item
                            className={'mt-4'}
                            rules={[
                              {
                                required: false,
                                message: 'Multiply flat shipping rate',
                              },
                            ]}
                          >
                            <Checkbox
                              name='is_flate_rate_multiply'
                              checked={isFilterFlatPrice}
                              onChange={(e) => setIsFilterFlatPrice(e.target.checked)}
                            >
                              Multiply flat shipping rate by Cart quantity
                            </Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                      
                    </>
                  )}
                  {ruleType == 4 && (
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Form.Item
                          className={'mb-0'}
                          label='Postal codes'
                          name='filter_postal_code'
                          rules={[
                            {
                              required: true,
                              message: 'Postal codes are required',
                            },
                          ]}
                        >
                          <Select
                            mode='tags'
                            style={{ width: '100%' }}
                            placeholder='Postal codes'
                            value={selectedPostalCodes}
                            onChange={handleSelectChange}
                            dropdownStyle={{ display: 'none' }}
                            onInput={(e) =>
                            (e.target.value = (
                              '' + e.target.value
                            ).toUpperCase())
                            }
                          >
                            {selectedPostalCodes.map((item) => (
                              <Option key={item} value={item}>
                                {item}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <div className={'text-gray mb-2'}>
                          Postal codes can be entered with exact values (e.g.,
                          90210), containing wildcards (e.g., 902*), or as fully
                          numeric ranges (e.g., 90210...99000). Use enter to add
                          the next value.
                        </div>
                      </Col>
                    </Row>
                  )}
                  {ruleType == 5 && (
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <div id='country_dropdown'>
                          <Form.Item
                            className={'mb-2'}
                            label='Warehouses'
                            name='warehouses'
                            rules={[
                              {
                                required: true,
                                message: 'Warehouses are required',
                              },
                            ]}
                          >
                            <Select
                              mode='multiple'
                              style={{ width: '100%' }}
                              placeholder='Select warehouses'
                              value={selectedWarehouses}
                              getPopupContainer={() =>
                                document.getElementById('country_dropdown')
                              }
                              allowClear
                              onChange={handleChangeWarehouses}
                              filterOption={filterOptionsWarehouses}
                            >
                              {warehouse
                                ? warehouse?.map((value) => (
                                  <Option
                                    value={value?.zip_code}
                                    key={value?.zip_code}
                                  >{`${value?.city + ','} ${value?.state} ${value?.zip_code
                                    }`}</Option>
                                ))
                                : null}
                            </Select>
                          </Form.Item>
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              )}

            {(ruleType == 1 || ruleType == 2 || ruleType == 3 || ruleType == 4 || ruleType == 5 || ruleType == 6 || ruleType == 7 || ruleType == 8 || ruleType == 10 || ruleType == 12) && (
              <>
                {ruleType == 2 && (
                  <>
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <div id='provider_dropdown_2'>
                          <Form.Item
                            className={'mb-2'}
                            label="Shipping provider's nickname"
                            name='filter_provider'
                            rules={[
                              {
                                required: true,
                                message: 'Shipping provider is required',
                              },
                            ]}
                          >
                            <Select
                              mode='multiple'
                              placeholder='Select installed providers'
                              value={this?.filter_provider || undefined}
                              onChange={handleProviderServices}
                              getPopupContainer={() =>
                                document.getElementById('provider_dropdown_2')
                              }
                            >
                              {installedCarriers?.filter(carrier =>
                                carrier.is_enabled === 1 || 
                                (Array.isArray(carrierSlug) && carrierSlug.includes(`${carrier?.slug}:${carrier?.id}`))
                              ).map((carrier) => (
                                <Option key={carrier?.id} value={`${carrier?.slug}:${carrier?.id}`}>
                                  {carrier.nickname || carrier.name}
                                </Option>
                              ))}
                              {warehouseProviderOptions.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>
                      </Col>
                    </Row>
                  </>
                )}
                {(ruleType == 1 || ruleType == 2 || ruleType == 6) && (
                  <>
                    {ruleType != 2 && (
                      <Row gutter={30}>
                        <Col
                          className='gutter-row'
                          xs={24}
                          sm={24}
                          md={24}
                          lg={24}
                          xl={24}
                        >
                          <div id='country_dropdown'>
                            <Form.Item
                              className={'mb-2'}
                              label="Shipping provider's nickname"
                              name='filter_provider'
                              rules={[
                                {
                                  required: true,
                                  message: 'Shipping provider is required',
                                },
                              ]}
                            >
                              <Select
                                placeholder='Select installed providers'
                                value={this?.filter_provider || undefined}
                                onChange={handleProviderServices}
                                getPopupContainer={() =>
                                  document.getElementById('country_dropdown')
                                }
                              >
                                {installedCarriers?.filter(isProviderOption).map((carrier) =>
                                  carrier?.slug == 'estes-ltl' ||
                                    carrier?.slug == 'abf-ltl' ||
                                    carrier?.slug == 'xpo-ltl' ||
                                    carrier?.slug == 'fedex-ltl' ||
                                    carrier?.slug == 'ups-ltl' ||
                                    carrier?.slug == 'ups-small' ||
                                    carrier?.slug == 'echo-ltl' ||
                                    carrier?.slug == 'odfl-ltl' ||
                                    carrier?.slug == 'daylight-ltl' ||
                                    carrier?.slug == 'ltl-quotes' ||
                                    carrier?.slug == 'rl-ltl' ||
                                    carrier?.slug == 'southeastern-ltl' ||
                                    carrier?.slug == 'saia-ltl' ||
                                    carrier?.slug == 'kn-ltl' ||
                                    carrier?.slug == "ct-ltl" ||
                                    carrier?.slug == 'road-runner' ||
                                    carrier?.slug == 'freightquote-chr-ltl' ||
                                    carrier?.slug == 'freightquote-ltl' ||
                                    carrier?.slug == 'ups-ship-engine' ||
                                    carrier?.slug == 'small-package' ||
                                    carrier?.slug == 'fedex-small' ||
                                    carrier?.slug == 'priority-one-ltl' ||
                                    carrier?.slug == 'unishippers-small' ||
                                    carrier?.slug == 'unishipper-ltl' ||
                                    carrier?.slug == 'usps-small' ||
                                    carrier?.slug == 'purolator-small' ||
                                    carrier?.slug == 'tql-ltl' ||
                                    carrier?.slug == 'gtz-ltl' ||
                                    carrier?.slug == 'dayross-ltl' ? (
                                    <Option value={carrier?.slug}>
                                      {carrier.nickname || carrier.name}
                                    </Option>
                                  ) : null
                                )}
                              </Select>
                            </Form.Item>
                          </div>
                        </Col>
                      </Row>
                    )}
                    {/* Hide Methods: LTL providers have no services, so the field is hidden when only LTL providers are selected. */}
                    {ruleType != 1 && !(ruleType == 2 && hasLtlProvider && !hasParcelProvider) && (
                    <>
                      <Row gutter={30}>
                        <Col
                          className='gutter-row'
                          xs={24}
                          sm={24}
                          md={24}
                          lg={24}
                          xl={24}
                        >
                          <div id='country_dropdown'>
                            <Form.Item
                              className={'mb-2'}
                              label={ruleType == 2 ? 'Provider services' : 'Service'}
                              name='filter_services'
                              rules={ruleType == 6 ? [
                                {
                                  required: true,
                                  message: 'Service is required',
                                },
                              ] : []}
                            >
                              <Select
                                mode='multiple'
                                style={{ width: '100%' }}
                                placeholder={ruleType == 2 ? 'Select provider services' : 'Select service'}
                                value={selectedServices}
                                onChange={handleChangeServices}
                                notFoundContent={
                                  <span>
                                    No service enabled. Please enable in quote settings.
                                  </span>
                                }
                                filterOption={filterServices}
                                getPopupContainer={() =>
                                  document.getElementById('country_dropdown')
                                }
                                allowClear
                                // Hide Methods only offers parcel services, so it needs a parcel provider.
                                disabled={ruleType == 2 ? !hasParcelProvider : !hasCarrierSelected}
                              >
                                {/* LTL accessorials - Override Rates only (see showLtlServiceOptions) */}
                                {showLtlServiceOptions && <Option value={'transportation'}>Transportation service</Option>}
                                {showLtlServiceOptions && <Option value={'residential'}>Residential delivery service</Option>}
                                {showLtlServiceOptions && accessorials && accessorials?.map((accessorial) => (
                                  accessorial?.accessorial_service_sac == 'lgd' && accessorial?.status ? (
                                    <Option value={'liftgate'}>Lift gate delivery service</Option>
                                  ) :
                                    accessorial?.accessorial_service_sac == 'nbd' && accessorial?.status ? (
                                      <Option value={'notify'}>Notify before delivery service</Option>
                                    ) :
                                      accessorial?.accessorial_service_sac == 'lad' && accessorial?.status ? (
                                        <Option value={'limitedAccess'}>Limited access delivery service</Option>
                                      ) :
                                        accessorial?.accessorial_service_sac == 'ind' && accessorial?.status ? (
                                          <Option value={'insideDelivery'}>Inside delivery service</Option>
                                        ) : null
                                ))}
                                {/* Parcel named services - shown whenever at least one selected provider is parcel */}
                                {hasParcelProvider &&
                                  carrierServicesLocal?.map((service) => (
                                    <Option
                                      key={service?.value}
                                      value={service?.value}
                                    >
                                      {service?.value}
                                    </Option>
                                  ))}
                              </Select>
                            </Form.Item>
                          </div>
                        </Col>
                      </Row>
                      {ruleType == 6 && (
                        <ServiceRate
                          label='Service rate (e.g. 5.25)'
                          name='service_rates'
                          placeholder='Enter service rate'
                          required={true}
                          message='Service rate is required'
                        />
                      )}
                      </>
                    )}
                    {/* Filters Heading for Override Rates - After Provider */}
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Title level={5}>Filters</Title>
                      </Col>
                    </Row>
                  </>
                )}
                {ruleType == 8 && (
                  <ServiceRate
                    label='Surcharge amount (e.g. 5.25)'
                    name='service_rates'
                    placeholder='Enter service rate'
                    required={true}
                    message='Surcharge amount is required'
                  />
                )}
                {/* Filters Heading for Surcharge - After Surcharge Amount */}
                {(ruleType == 8 || ruleType == 10) && (
                  <Row gutter={30}>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                      <Title level={5}>Filters</Title>
                    </Col>
                  </Row>
                )}
                {/* Filter by Weight - Only for rule types 1, 2, 6, 8, 10 */}
                {(ruleType == 1 || ruleType == 2 || ruleType == 6 || ruleType == 8 || ruleType == 10) && (
                  <Row gutter={30}>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                      <Form.Item className={'mb-0'} name='is_filter_weight'>
                        <Checkbox
                          name='is_filter_weight'
                          checked={isFilterWeight}
                          onChange={(e) => setIsFilterWeight(e.target.checked)}
                        >
                          Filter by weight (lbs)
                        </Checkbox>
                        <div className={'text-gray mb-2'}>
                          Enable the checkbox and enter a weight range in lbs; the From field (accepts zero) is required, and leave the To field blank to avoid an upper limit.
                        </div>
                      </Form.Item>
                    </Col>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={12}
                      lg={12}
                      xl={12}
                    >
                      <Form.Item
                        label='From'
                        name='weight_from'
                        rules={[
                          {
                            required: isFilterWeight,
                            message: 'Weight from is required',
                          },
                          {
                            pattern: /^(\d{0,10}(\.\d{0,2})?)?$/,
                            message: 'Please enter up to 10 numbers with 2 decimal places.',
                          },
                        ]}
                      >
                        <Input
                          type='number'
                          onKeyDown={blockInvalidChar}
                          step='0.01'
                          min={0}
                          placeholder='Enter weight from'
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={12}
                      lg={12}
                      xl={12}
                    >
                      <Form.Item
                        label='To'
                        name='weight_to'
                        rules={[
                          {
                            required: false,
                            message: 'Weight to is required',
                          },
                          {
                            pattern: /^(\d{0,10}(\.\d{0,2})?)?$/,
                            message: 'Please enter up to 10 numbers with 2 decimal places.',
                          },
                        ]}
                      >
                        <Input
                          type='number'
                          onKeyDown={blockInvalidChar}
                          step='0.01'
                          min={0}
                          placeholder='Enter weight to'
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                {/* Filter by Price - Only for rule types 1, 2, 6, 8, 10 */}
                {(ruleType == 1 || ruleType == 2 || ruleType == 6 || ruleType == 8 || ruleType == 10) && (
                  <>
                  <Row gutter={30}>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                      <Form.Item className={'mb-0'} name='is_filter_price'>
                        <Checkbox
                          name='is_filter_price'
                          checked={isFilterPrice}
                          onChange={(e) => setIsFilterPrice(e.target.checked)}
                        >
                          Filter by price
                        </Checkbox>
                        <div className={'text-gray mb-2'}>
                          Enable the checkbox and enter a price range in US dollars; the From field (accepts zero) is required, and leave the To field blank to avoid an upper limit.
                        </div>
                      </Form.Item>
                    </Col>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={12}
                      lg={12}
                      xl={12}
                    >
                      <Form.Item
                        label='From'
                        name='price_from'
                        rules={[
                          {
                            required: isFilterPrice,
                            message: 'Price from is required',
                          },
                          {
                            pattern: /^(\d{0,15}(\.\d{0,2})?)?$/,
                            message: 'Please enter up to 15 numbers with 2 decimal places.',
                          },
                        ]}
                      >
                        <Input
                          type='number'
                          onKeyDown={blockInvalidChar}
                          step='0.01'
                          min={0}
                          placeholder='Enter price from'
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={12}
                      lg={12}
                      xl={12}
                    >
                      <Form.Item
                        label='To'
                        name='price_to'
                        rules={[
                          {
                            required: false,
                            message: 'Price to is required',
                          },
                          {
                            pattern: /^(\d{0,15}(\.\d{0,2})?)?$/,
                            message: 'Please enter up to 15 numbers with 2 decimal places.',
                          },
                        ]}
                      >
                        <Input
                          type='number'
                          onKeyDown={blockInvalidChar}
                          step='0.01'
                          min={0}
                          placeholder='Enter price to'
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                    <div id='price_dropdown'>
                      <Form.Item
                        label='Price Basis'
                        name='priceBasis'
                        rules={[
                          {
                            required: false,
                            message: 'Price Basis is required',
                          },
                        ]}
                      >
                        <Select
                          placeholder='Price Basis'
                          disabled={!isFilterPrice}

                          getPopupContainer={() =>
                            document.getElementById('price_dropdown')
                          }
                        >
                          <Option value={'regular'}>Regular Price</Option>
                          <Option value={'discounted'}>Discounted Price</Option>
                        </Select>
                      </Form.Item>
                        <div className={'text-gray mb-2'}>
                          Select which product price to use when evaluating the price filter. Regular Price uses prices before discounts; Discounted Price uses applicable prices after discounts.
                        </div>
                    </div>
                    </Col>
                  </Row>     
                </>
                )}
                {/* Filter by Quantity - Only for rule types 1, 2, 6, 8, 10 */}
                {(ruleType == 1 || ruleType == 2 || ruleType == 6 || ruleType == 8 || ruleType == 10) && (
                  <Row gutter={30}>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                      <Form.Item className={'mb-0'} name='is_filter_quantity'>
                        <Checkbox
                          name='is_filter_quantity'
                          checked={isFilterQuantity}
                          onChange={(e) => setIsFilterQuantity(e.target.checked)}
                        >
                          Filter by quantity
                        </Checkbox>
                        <div className={'text-gray mb-2'}>
                          Enable the checkbox and enter a quantity range in whole numbers (no decimals); the From field (accepts zero) is required, and leave the To field blank to avoid an upper limit.
                        </div>
                      </Form.Item>
                    </Col>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={12}
                      lg={12}
                      xl={12}
                    >
                      <Form.Item
                        label='From'
                        name='quantity_from'
                        rules={[
                          {
                            required: isFilterQuantity,
                            message: 'Quantity from is required',
                          },
                          {
                            pattern: /^(\d{0,7}(\.\d{0,2})?)?$/,
                            message: 'Please enter up to 7 numbers.',
                          },
                        ]}
                      >
                        <Input
                          type='number'
                          onKeyDown={handleNumbersOnly}
                          step='1'
                          min={0}
                          placeholder='Enter quantity from'
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      className='gutter-row'
                      xs={24}
                      sm={24}
                      md={12}
                      lg={12}
                      xl={12}
                    >
                      <Form.Item
                        label='To'
                        name='quantity_to'
                        rules={[
                          {
                            required: false,
                            message: 'Quantity to is required',
                          },
                          {
                            pattern: /^(\d{0,7}(\.\d{0,2})?)?$/,
                            message: 'Please enter up to 7 numbers.',
                          },
                        ]}
                      >
                        <Input
                          type='number'
                          onKeyDown={handleNumbersOnly}
                          step='1'
                          min={0}
                          placeholder='Enter quantity to'
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                {/* Filters Heading for Flat Shipping Rate and other rule types */}
                {(ruleType == 3 || ruleType == 4 || ruleType == 5 || ruleType == 7 || ruleType == 12) && (
                  <Row gutter={30}>
                    <Col
                      className='gutter-row mt-2'
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                    >
                      <Title level={5}>Filters</Title>
                    </Col>
                  </Row>
                )}
                {/* Filter by Category */}
                <Row gutter={30}>
                  <Col
                    className='gutter-row'
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                  >
                    <Form.Item className={'mb-0'} name='is_filter_category' valuePropName='checked'>
                      <Checkbox
                        name='is_filter_category'
                        checked={isFilterCategory}
                        onChange={(e) => setIsFilterCategory(e.target.checked)}
                      >
                        Filter by category
                      </Checkbox>
                      <div className={'text-gray mb-2'}>
                        Enable the checkbox to filter the rule by specific categories.
                      </div>
                    </Form.Item>
                  </Col>
                  <Col
                    className='gutter-row'
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                  >
                    <div id='category_dropdown'>
                      <Form.Item
                        className={'mb-2'}
                        // label={'Select categories'}
                        name='filter_categories'
                        rules={[
                          {
                            required: isFilterCategory,
                            message: 'Categories are required',
                          },
                        ]}
                      >
                        <Select
                          mode='multiple'
                          style={{ width: '100%' }}
                          placeholder={'Select categories'}
                          value={selectedCategories}
                          getPopupContainer={() =>
                            document.getElementById('category_dropdown')
                          }
                          allowClear
                          onChange={handleChangeCategories}
                          filterOption={filterOptionsCategories}
                        >
                          {storeCategories?.map((option) => (
                            <Option key={option.key} value={option.key}>
                              {option.value}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
                {/* Filter by Brand */}
                <Row gutter={30}>
                  <Col
                    className='gutter-row'
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                  >
                    <Form.Item className={'mb-0'} name='is_filter_brand' valuePropName='checked'>
                      <Checkbox
                        name='is_filter_brand'
                        checked={isFilterBrand}
                        onChange={(e) => setIsFilterBrand(e.target.checked)}
                      >
                        Filter by brand
                      </Checkbox>
                      <div className={'text-gray mb-2'}>
                        Enable the checkbox to filter the rule by specific brands.
                      </div>
                    </Form.Item>
                  </Col>
                  <Col
                    className='gutter-row'
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                  >
                    <div id='brand_dropdown'>
                      <Form.Item
                        className={'mb-2'}
                        // label={'Select brands'}
                        name='filter_brands'
                        rules={[
                          {
                            required: isFilterBrand,
                            message: 'Brands are required',
                          },
                        ]}
                      >
                        <Select
                          mode='multiple'
                          style={{ width: '100%' }}
                          placeholder={'Select brands'}
                          value={selectedBrands}
                          getPopupContainer={() =>
                            document.getElementById('brand_dropdown')
                          }
                          allowClear
                          onChange={handleChangeBrands}
                          filterOption={filterOptionsBrands}
                        >
                          {storeBrands?.map((option) => (
                            <Option key={option.key} value={option.key}>
                              {option.value}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
                {/* Filter by Product */}
                <Row gutter={30}>
                  <Col
                    className='gutter-row'
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                  >
                    <Form.Item className={'mb-0'} name='is_filter_product' valuePropName='checked'>
                      <Checkbox
                        name='is_filter_product'
                        checked={isFilterProduct}
                        onChange={(e) => setIsFilterProduct(e.target.checked)}
                      >
                        Filter by product
                      </Checkbox>
                      <div className={'text-gray mb-2'}>
                        Enable the checkbox to filter the rule by specific products.
                      </div>
                    </Form.Item>
                  </Col>
                  <Col
                    className='gutter-row'
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                  >
                    <div id='product_dropdown'>
                      <div ref={dropdownRef}>
                        <Form.Item
                          className={'mb-2'}
                          // label={'Select products'}
                          name='filter_products'
                          rules={[
                            {
                              required: isFilterProduct,
                              message: 'Products are required',
                            },
                          ]}
                        >
                          <Select
                            mode='multiple'
                            style={{ width: '100%' }}
                            placeholder={'Search products by name, SKU'}
                            labelInValue
                            getPopupContainer={() =>
                              document.getElementById('product_dropdown')
                            }
                            notFoundContent={
                              loading ? (
                                <span>
                                  <Spin size='small' />
                                </span>
                              ) : searchQuery?.length > 2 &&
                                data?.length == 0 ? (
                                <span>Product not found!</span>
                              ) : (
                                <span>
                                  Please enter a minimum of three characters.
                                </span>
                              )
                            }
                            value={selectedProducts}
                            allowClear
                            onSearch={handleSearch}
                            onChange={handleChangeProducts}
                            filterOption={false}
                          >
                            {data?.map((option) => (
                              <Option
                                key={option?.source_product_id}
                                value={option?.source_product_id}
                              >
                                {option?.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </div>
                  </Col>
                </Row>
                {/* Filter by Customer Group */}
                {(ruleType == 1 || ruleType == 2 || ruleType == 3 || ruleType == 4 || ruleType == 5 || ruleType == 6 || ruleType == 7 || ruleType == 8 || ruleType == 10) && (
                  <>
                    <Row gutter={30}>
                      <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                        <Form.Item className={'mb-0'} name='is_filter_customer_group'>
                          <Checkbox
                            name='is_filter_customer_group'
                            checked={isFilterCustomerGroup}
                            onChange={(e) => setIsFilterCustomerGroup(e.target.checked)}
                          >
                            Filter by customer group
                          </Checkbox>
                          <div className={'text-gray mb-2'}>
                            Enable the checkbox to filter the rule by specific customer groups.
                          </div>
                        </Form.Item>
                      </Col>
                      <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
                        <Form.Item
                          className={'mb-2'}
                          name='filter_customer_groups'
                          rules={[
                            {
                              required: isFilterCustomerGroup,
                              message: 'Customer groups are required',
                            },
                          ]}
                        >
                          <Select
                            mode='multiple'
                            placeholder='Select customer groups'
                            value={selectedCustomerGroups}
                            onChange={(value) => setSelectedCustomerGroups(value)}
                            filterOption={(input, option) =>
                              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {(customerGroups || []).map((group) => (
                              <Option key={group.key} value={group.key}>
                                {group.value}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
                {/* Filter by Location */}
                {(ruleType == 1 || ruleType == 2 || ruleType == 6 || ruleType == 8 || ruleType == 10) && (
                  <>
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Form.Item className={'mb-0'} name='is_filter_location'>
                          <Checkbox
                            name='is_filter_location'
                            checked={isFilterLocation}
                            onChange={(e) => setIsFilterLocation(e.target.checked)}
                          >
                            Filter by location
                          </Checkbox>
                          <div className={'text-gray mb-2'}>
                            Enable the checkbox to filter the rule by geography. The rule will apply only if the ship-to address is in the geography identified in the filter.
                          </div>
                        </Form.Item>
                      </Col>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <div id='filter_location_country_dropdown'>
                          <Form.Item
                            className={'mb-2'}
                            label='Countries'
                            name='filter_country'
                          // rules={[
                          //   {
                          //     required: isFilterLocation,
                          //     message: 'Countries are required',
                          //   },
                          // ]}
                          >
                            <Select
                              mode='multiple'
                              placeholder='Select countries'
                              value={countryCode}
                              disabled={!isFilterLocation}
                              onChange={(value) => handleChangeCountryLocation(value)}
                              getPopupContainer={() =>
                                document.getElementById('filter_location_country_dropdown')
                              }
                              allowClear
                            >
                              <Option value={'US'}>United States</Option>
                              <Option value={'CA'}>Canada</Option>
                            </Select>
                          </Form.Item>
                        </div>
                      </Col>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <div id='filter_location_state_dropdown'>
                          <Form.Item
                            className={'mb-2'}
                            label='States/Provinces'
                            name='filter_state_province'
                            rules={[
                              {
                                required: false,
                                message: 'States/Provinces are required',
                              },
                            ]}
                          >
                            <Select
                              mode='multiple'
                              style={{ width: '100%' }}
                              placeholder='Select states/provinces'
                              value={selectedProvinces}
                              disabled={
                                !isFilterLocation ||
                                ((!countryCode?.length && !form.getFieldValue('filter_country')?.length) &&
                                 (!selectedProvinces?.length && !form.getFieldValue('filter_state_province')?.length))
                              }

                              allowClear
                              onChange={handleChangeProvinces}
                              filterOption={filterOptionsProvinces}
                              getPopupContainer={() =>
                                document.getElementById('filter_location_state_dropdown')
                              }
                            >
                              {statesProvince?.map((option) => (
                                <Option key={option.key} value={option.value}>
                                  {option.value}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>
                      </Col>
                      {ruleType == 1 && (
                        <Col
                          className='gutter-row'
                          xs={24}
                          sm={24}
                          md={24}
                          lg={24}
                          xl={24}
                        >
                          <Form.Item
                            className={'mb-0'}
                            label='Postal codes'
                            name='filter_postal_code'
                            rules={[
                              {
                                required: false,
                                message: 'Postal codes are required',
                              },
                            ]}
                          >
                            <Select
                              mode='tags'
                              style={{ width: '100%' }}
                              placeholder='Postal codes'
                              value={selectedPostalCodes}
                              disabled={!isFilterLocation}
                              onChange={handleSelectChange}
                              dropdownStyle={{ display: 'none' }}
                              onInput={(e) =>
                              (e.target.value = (
                                '' + e.target.value
                              ).toUpperCase())
                              }
                            >
                              {selectedPostalCodes.map((item) => (
                                <Option key={item} value={item}>
                                  {item}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <div className={'text-gray mb-2'}>
                            Postal codes can be entered with exact values (e.g.,
                            90210), containing wildcards (e.g., 902*), or as fully
                            numeric ranges (e.g., 90210...99000). Use enter to add
                            the next value.
                          </div>
                        </Col>
                      )}
                    </Row>
                  </>
                )}
                {/* Filter by Maximum Shipping Rate */}
                {(ruleType == 6) && (
                  <>
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Form.Item className={'mb-0'} name='is_filter_max_shipping_rate'>
                          <Checkbox
                            name='is_filter_max_shipping_rate'
                            checked={isFilterMaxShippingRate}
                            onChange={(e) => setIsFilterMaxShippingRate(e.target.checked)}
                          >
                            Filter by maximum shipping rate
                          </Checkbox>
                          <div className={'text-gray mb-2'}>
                            Enable the checkbox to filter the rule by a maximum shipping rate.
                          </div>
                        </Form.Item>
                      </Col>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Form.Item
                          className={'mb-2'}
                          label='Maximum Shipping Rate'
                          name='filter_max_shipping_rate'
                          rules={[
                            {
                              required: isFilterMaxShippingRate,
                              message: 'Maximum shipping rate is required',
                            },
                            {
                              pattern: /^(\d{0,15}(\.\d{0,2})?)?$/,
                              message: 'Please enter up to 15 numbers with 2 decimal places.',
                            },
                          ]}
                        >
                          <Input
                            type='number'
                            onKeyDown={blockInvalidChar}
                            step='0.01'
                            min={0}
                            placeholder='Enter maximum shipping rate'
                            disabled={!isFilterMaxShippingRate}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
                {/* Filter by Address Type */}
                {(ruleType == 1 || ruleType == 2 || ruleType == 6 || ruleType == 7 || ruleType == 8 || ruleType == 10) && (
                  <>
                    <Row gutter={30}>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Form.Item className={'mb-0'} name='is_address_type'>
                          <Checkbox
                            name='is_address_type'
                            checked={isAddressType}
                            onChange={(e) => setIsAddressType(e.target.checked)}
                          >
                            Filter by address type{' '}
                            <Tooltip
                              title="Residential and Commercial address options are available only when the Automatically detect residential addresses setting is enabled in Address Type Settings. This allows rule type to be applied based on whether the delivery address is Commercial or Residential."
                              overlayInnerStyle={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                              }}
                              color="#ffffff"
                            >
                              <span style={{
                                cursor: 'help',
                                color: '#000000',
                                backgroundColor: '#ffffff',
                                border: '1px solid #d9d9d9',
                                borderRadius: '2px',
                                padding: '0 4px',
                                display: 'inline-block',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>[?]</span>
                            </Tooltip>
                          </Checkbox>
                        </Form.Item>
                      </Col>
                      <Col
                        className='gutter-row'
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        <Form.Item
                          className={'mb-2'}
                          // label='Address Type'
                          name='selected_address_type'
                          initialValue={0}
                        >
                          <Select
                            placeholder='Select address type'
                            disabled={!isAddressType}
                            value={selectedAddressType}
                            onChange={(value) => setSelectedAddressType(value)}
                            defaultValue={0}
                            style={{
                              opacity: isAddressType ? 1 : 1
                            }}
                          >
                            <Option value={0}>All address types (default)</Option>
                            {(() => {
                              // Check if RAD is enabled
                              const isRadInstalled = radPlans?.currentPackage?.status === 1 || radPlans?.currentPackage?.status === '1';
                              // RAD is enabled if installed AND settings don't force always_quote_residential_delivery
                              // Default to enabled when RAD is installed but settings aren't loaded yet (new store case)
                              let radEnabled = false;
                              if (isRadInstalled) {
                                if (radSettings?.settings) {
                                  try {
                                    const settings = JSON.parse(radSettings.settings);
                                    radEnabled = !settings?.always_quote_residential_delivery;
                                  } catch (e) {
                                    // If parsing fails, default to enabled when RAD is installed
                                    radEnabled = true;
                                  }
                                } else {
                                  // Settings not loaded yet or don't exist - default to enabled when RAD is installed
                                  radEnabled = true;
                                }
                              }

                              if (radEnabled && isRadInstalled) {
                                return (
                                  <>
                                    <Option value={1}>Commercial addresses</Option>
                                    <Option value={2}>Residential addresses</Option>
                                    {ruleType == 2 && (
                                      <Option value={3}>PO/APO/DPO/FPO addresses</Option>
                                    )}
                                  </>
                                );
                              }
                              return null;
                            })()}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}

            {/* Filter Logic - Show for Restrict to Country, Hide Methods, Restrict to State, Restrict to Postal Codes, Restrict to Origin Locations, Override Rates and Surcharge */}
            {(ruleType == 1 || ruleType == 2 || ruleType == 3 || ruleType == 4 || ruleType == 5 || ruleType == 6 || ruleType == 8 || ruleType == 10 || ruleType == 7 || ruleType == 12) && (
              <Row gutter={30}>
                <Col
                  className='gutter-row'
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                >
                  <Form.Item className={'mb-2'}>
                    <Radio.Group
                      value={filterLogic}
                      onChange={(e) => setFilterLogic(e.target.value)}
                    >
                      <div>
                        <Radio value='all'>
                          Apply rule only if all the filters are met
                        </Radio>
                      </div>
                      <div className='mt-1'>
                        <Radio value='any'>
                          Apply rule if any of the filters are met
                        </Radio>
                      </div>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={30}>
              <Col
                className='gutter-row'
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
              >
                <Form.Item
                  className={'mb-2'}
                  rules={[
                    {
                      required: false,
                      message: 'Available',
                    },
                  ]}
                >
                  <Checkbox
                    name='available'
                    checked={available}
                    onChange={(e) => setAvailable(e.target.checked)}
                  >
                    Available
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={30} align='middle' className={'mt-3'}>
              <Col
                className='gutter-row'
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
              >
                <Form.Item
                  style={{
                    textAlign: 'right',
                    marginBottom: '0',
                  }}
                >
                  <Space>
                    <Button type='primary' size={'large'} htmlType='submit'>
                      Save
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal
        title='Confirm Delete'
        centered
        visible={modal.open && modal.type === 'delete'}
        onOk={() => dispatch(deleteShippingRule(shippingRuleId, token))}
        onCancel={() => hanldeModalToggling(false, '')}
        okText='Confirm'
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>Are you sure you want to delete this shipping rule?</p>
      </Modal>
    </Fragment>
  );
}

export default ShippingRulesComponent;
